import { SaveData } from "./Storage";

/**
 * Client for the /api/auth/* and /api/progress endpoints.
 *
 * Session token is stored in localStorage so the player stays signed in
 * across visits. Progress is server-authoritative — we keep a debounced
 * write-through to /api/progress so coins/best score persist across devices.
 */

const TOKEN_KEY = "suedegod.session.v1";
const EMAIL_KEY = "suedegod.email.v1";
const GUEST_KEY = "suedegod.guest.v1";
const SYNC_DEBOUNCE_MS = 1200;

export interface AuthSubmitResult {
  ok: boolean;
  error?: string;
  until?: string;
}

export class Account {
  private token: string | null = null;
  private email: string | null = null;
  private syncTimer: number | null = null;
  private pending: SaveData | null = null;
  private inflight: Promise<void> | null = null;

  constructor() {
    try {
      this.token = localStorage.getItem(TOKEN_KEY);
      this.email = localStorage.getItem(EMAIL_KEY);
    } catch {
      this.token = null;
      this.email = null;
    }

    // Best-effort flush on page hide so the last few coin/score updates
    // make it to the server even if the user closes the tab quickly.
    window.addEventListener("pagehide", () => this.beaconFlush());
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.beaconFlush();
    });
  }

  hasSession(): boolean {
    return !!this.token;
  }
  getEmail(): string | null {
    return this.email;
  }

  /** Persistent flag set when the player chose "Play as Guest" so we
   *  don't show the auth screen on every subsequent visit. */
  isGuest(): boolean {
    if (this.token) return false; // signed-in trumps guest flag
    try {
      return localStorage.getItem(GUEST_KEY) === "1";
    } catch {
      return false;
    }
  }
  setGuest(): void {
    try { localStorage.setItem(GUEST_KEY, "1"); } catch { /* ignore */ }
  }
  clearGuest(): void {
    try { localStorage.removeItem(GUEST_KEY); } catch { /* ignore */ }
  }

  private headers(json = true): HeadersInit {
    const h: Record<string, string> = {};
    if (json) h["Content-Type"] = "application/json";
    if (this.token) h["Authorization"] = `Bearer ${this.token}`;
    return h;
  }

  private setSession(token: string, email: string): void {
    this.token = token;
    this.email = email;
    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(EMAIL_KEY, email);
      localStorage.removeItem(GUEST_KEY);
    } catch {
      /* private mode — keep in memory only */
    }
  }

  private clearSession(): void {
    this.token = null;
    this.email = null;
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(EMAIL_KEY);
    } catch {
      /* ignore */
    }
  }

  /**
   * Validate the existing session against the server. Returns true if the
   * stored token still works. On network failure we keep the local token so
   * offline play with the last-known progress still works.
   */
  async restore(): Promise<boolean> {
    if (!this.token) return false;
    try {
      const r = await fetch("/api/auth/me", { headers: this.headers(false) });
      if (r.status === 401) {
        this.clearSession();
        return false;
      }
      if (!r.ok) return !!this.token;
      const j = (await r.json()) as { email?: string };
      if (j.email) {
        this.email = j.email;
        try { localStorage.setItem(EMAIL_KEY, j.email); } catch { /* ignore */ }
      }
      return true;
    } catch {
      return !!this.token;
    }
  }

  async signup(email: string, pin: string): Promise<AuthSubmitResult> {
    return this.authCall("/api/auth/signup", email, pin);
  }
  async login(email: string, pin: string): Promise<AuthSubmitResult> {
    return this.authCall("/api/auth/login", email, pin);
  }

  private async authCall(url: string, email: string, pin: string): Promise<AuthSubmitResult> {
    let r: Response;
    try {
      r = await fetch(url, {
        method: "POST",
        headers: this.headers(true),
        body: JSON.stringify({ email, pin }),
      });
    } catch {
      return { ok: false, error: "network" };
    }
    const j = (await r.json().catch(() => ({}))) as {
      token?: string;
      email?: string;
      error?: string;
      until?: string;
    };
    if (!r.ok || !j.token || !j.email) {
      return { ok: false, error: j.error, until: j.until };
    }
    this.setSession(j.token, j.email);
    return { ok: true };
  }

  async logout(): Promise<void> {
    if (this.token) {
      try {
        await fetch("/api/auth/logout", { method: "POST", headers: this.headers(false) });
      } catch {
        /* ignore */
      }
    }
    this.clearSession();
    this.pending = null;
    if (this.syncTimer != null) {
      window.clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  /** Fetch server progress for the signed-in user. Returns null if empty. */
  async fetchProgress(): Promise<SaveData | null> {
    if (!this.token) return null;
    try {
      const r = await fetch("/api/progress", { headers: this.headers(false) });
      if (r.status === 401) {
        this.clearSession();
        return null;
      }
      if (!r.ok) return null;
      const j = (await r.json()) as { data: unknown };
      if (!j.data || typeof j.data !== "object" || Object.keys(j.data as object).length === 0) {
        return null;
      }
      return j.data as SaveData;
    } catch {
      return null;
    }
  }

  /** Debounced save; the latest call wins. */
  queueSave(data: SaveData): void {
    if (!this.token) return;
    this.pending = data;
    if (this.syncTimer != null) window.clearTimeout(this.syncTimer);
    this.syncTimer = window.setTimeout(() => this.flush(), SYNC_DEBOUNCE_MS);
  }

  async flush(): Promise<void> {
    if (this.inflight) return this.inflight;
    if (!this.token || !this.pending) return;
    const data = this.pending;
    this.pending = null;
    this.syncTimer = null;
    this.inflight = (async () => {
      try {
        const r = await fetch("/api/progress", {
          method: "POST",
          headers: this.headers(true),
          body: JSON.stringify({ data }),
        });
        if (r.status === 401) {
          this.clearSession();
        } else if (!r.ok) {
          // Keep pending so the next change re-queues this payload.
          this.pending = data;
        }
      } catch {
        this.pending = data;
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }

  /** Synchronous best-effort flush during page hide. */
  private beaconFlush(): void {
    if (!this.token || !this.pending) return;
    try {
      const body = new Blob([JSON.stringify({ data: this.pending })], {
        type: "application/json",
      });
      // sendBeacon doesn't support custom headers, so we encode the token in
      // the URL query. The server accepts ?t=<token> as a fallback to Bearer.
      // (Falls back to keepalive fetch if sendBeacon isn't available.)
      const url = `/api/progress?t=${encodeURIComponent(this.token)}`;
      const sent = navigator.sendBeacon?.(url, body) ?? false;
      if (!sent) {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: this.pending }),
          keepalive: true,
        }).catch(() => {});
      }
      this.pending = null;
    } catch {
      /* ignore */
    }
  }
}
